import React, {useCallback, useEffect} from "react";
import styled from "styled-components";
import {ISquad} from "apps/DRAW_PREDICTOR/modules/reducers";
import {useDispatch, useSelector} from "react-redux";
import {
	checkIsPotFull,
	getActivePot,
	getAvailableGroupForPot1,
	getPredictionBySquadId,
	getSelectedSquadId,
} from "apps/DRAW_PREDICTOR/modules/selectors";
import {addPrediction, setActivePot, setSelectedSquad} from "apps/DRAW_PREDICTOR/modules/actions";
import {media} from "assets/css/media";
import {trackAdobeAnalytics} from "modules";
import SquadBlock from "apps/DRAW_PREDICTOR/assets/images/icons/squadBlock.svg";
import SquadBlockActive from "apps/DRAW_PREDICTOR/assets/images/icons/squadBlockActive.svg";
import {showFanFact} from "apps/DRAW_PREDICTOR/modules/actions/modals";
import {getUserSupportedCountry} from "modules/selectors";
import {FLAGS_URL} from "apps/DRAW_PREDICTOR/modules/constants";

const CountryBlock = styled.button`
	width: 70px;
	height: 90px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	background: url(${SquadBlock}) no-repeat;
	border-radius: 5px;
	cursor: pointer;
	margin: 0 10px;
	border: none;

	&:disabled {
		opacity: 0.4;
	}

	&:hover {
		background-image: url(${SquadBlockActive});
		filter: drop-shadow(0px 4px 10px rgba(0, 0, 0, 0.25));
	}

	@media (max-width: ${media.mobile}) {
		display: inline-flex;
		width: 68px;
		height: 75px;
		margin-bottom: 10px;
	}
`;

const CountryName = styled.div`
	font-size: 16px;
	font-weight: 500;
	align-items: center;
`;

interface IProps {
	squad: ISquad;
}

const GROUPS_TO_SHOW_FAN_FACT = ["d", "h"];

const Flag = styled.div`
	width: 60px;
	img {
		width: auto;
		max-width: 100%;
	}
	@media (max-width: ${media.mobile}) {
		width: 44px;
	}
	@media (max-width: 320px) {
		height: 30px;
	}
`;

export const Squad: React.FC<IProps> = ({squad}) => {
	const dispatch = useDispatch();
	const selectedSquad = useSelector(getSelectedSquadId);
	const activePot = useSelector(getActivePot);
	const availableGroupForActivePot = useSelector(getAvailableGroupForPot1);
	const isCurrentSquadSelected = Boolean(useSelector(getPredictionBySquadId)(squad.id));
	const isPotFull = useSelector(checkIsPotFull)(activePot);
	const userSupportedCountry = useSelector(getUserSupportedCountry);

	const selectedSquadIsNotCurrentSquad = Boolean(selectedSquad && selectedSquad !== squad.id);

	const isDisabled = Boolean(selectedSquadIsNotCurrentSquad || isCurrentSquadSelected);
	const handleChangeSelectedSquad = useCallback(() => {
		if (activePot === 1) {
			trackAdobeAnalytics({
				section: "",
				eventName: "drawTeamChoice",
				optional: {
					team: squad.name,
					pot: `Pot 1`,
					group: `Group ${availableGroupForActivePot.toUpperCase()}`,
					selection: `${availableGroupForActivePot.toUpperCase()}1`,
				},
			});

			if (
				GROUPS_TO_SHOW_FAN_FACT.includes(availableGroupForActivePot) ||
				userSupportedCountry === squad.shortName
			) {
				dispatch(showFanFact(squad.id));
			}

			dispatch(
				addPrediction({
					squadId: squad.id,
					namegroup: availableGroupForActivePot,
					position: 1,
				})
			);
		} else {
			dispatch(setSelectedSquad(squad.id));
		}
	}, [dispatch, squad, availableGroupForActivePot, activePot, userSupportedCountry]);

	useEffect(() => {
		if (activePot !== 4 && isPotFull) {
			const nextPot = activePot + 1;
			dispatch(setActivePot(nextPot));
		}
	}, [dispatch, isPotFull, activePot]);
	const imageUrl = `${FLAGS_URL}${squad.shortName}.png`;

	return (
		<CountryBlock disabled={isDisabled} onClick={handleChangeSelectedSquad}>
			<Flag>
				<img src={imageUrl} alt={squad.name} />
			</Flag>
			<CountryName>{squad.shortName}</CountryName>
		</CountryBlock>
	);
};
